import type { FinancialSituationModel } from "@repo/shared/types";
import { useState } from "react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { useFinancialSituationModels } from "@/lib/hooks/use-financial-situation-models";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { FinancialSituationModelsLoader } from "./financial-situation-models-loader";
import { ModelDialog } from "./model-dialog";
import { ModelsTable } from "./models-table";
import { T } from "./models-wrapper.i18n";

export function FinancialSituationModelsWrapper() {
  const { tenantDefaultLanguage, tenantSupportedLanguages } = useUserLanguage();
  const t = useT(T);
  const { models, isLoading } = useFinancialSituationModels();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<FinancialSituationModel | null>(null);

  const handleCreate = () => {
    setEditingModel(null);
    setDialogOpen(true);
  };

  const handleEdit = (model: FinancialSituationModel) => {
    setEditingModel(model);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingModel(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[
          { label: t.settings, href: "/settings" },
          { label: t.financialSituationModels },
        ]}
        title={t.financialSituationModels}
        description={t.pageDescription}
      />

      <div className="flex justify-end mb-4">
        <Button onClick={handleCreate}>{t.createModel}</Button>
      </div>

      {isLoading && models.length === 0 ? (
        <FinancialSituationModelsLoader />
      ) : (
        <ModelsTable models={models} onEdit={handleEdit} />
      )}

      <ModelDialog
        open={dialogOpen}
        model={editingModel}
        supportedLanguages={tenantSupportedLanguages}
        defaultLanguage={tenantDefaultLanguage}
        onClose={handleDialogClose}
      />
    </div>
  );
}
